
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import StickyCTA from '../../components/feature/StickyCTA';
import ArticleContent from './components/ArticleContent';
import RelatedArticles from './components/RelatedArticles';
import ArticleJsonLd from '../../components/seo/ArticleJsonLd';
import ArticleDocumentHead from '../../components/seo/ArticleDocumentHead';
import BreadcrumbJsonLd from '../../components/seo/BreadcrumbJsonLd';
import { buildArticleDetailBreadcrumbs } from '../../utils/breadcrumbJsonLd';
import { defaultOriginForJsonLd } from '../../utils/organizationJsonLd';
import {
  fetchPublishedArticles,
  fetchPublishedArticleById,
  readArticleBootstrapFromDom,
  clearPublishedArticlesSessionCache,
  Article,
} from '../../utils/articleApi';

function readBootstrapForId(id: string | undefined): Article | null {
  if (!id) return null;
  const bootstrap = readArticleBootstrapFromDom();
  if (bootstrap && String(bootstrap.id) === String(id)) {
    return bootstrap;
  }
  return null;
}

export default function ArticleDetail() {
  const { id: idParam } = useParams<{ id: string }>();
  const location = useLocation();
  /** useParams が一瞬だけ欠けるケースへのフォールバック */
  const id =
    idParam ?? /^\/articles\/([^/]+)\/?$/.exec(location.pathname)?.[1];
  const [article, setArticle] = useState<Article | null>(() => readBootstrapForId(id));
  /** 関連記事用。fetchPublishedArticles は1回のみ（RelatedArticles で再取得しない） */
  const [publishedArticles, setPublishedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(() => !readBootstrapForId(id));

  const articleBreadcrumbItems = useMemo(() => {
    if (!article) return null;
    return buildArticleDetailBreadcrumbs(
      defaultOriginForJsonLd(),
      location.pathname,
      article.title
    );
  }, [article, location.pathname]);

  useEffect(() => {
    const loadArticle = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      const hadBootstrap = !!readBootstrapForId(id);
      if (!hadBootstrap) {
        setLoading(true);
      }
      try {
        const [index, detail] = await Promise.all([
          fetchPublishedArticles(),
          fetchPublishedArticleById(id),
        ]);
        if (index.length === 0) {
          clearPublishedArticlesSessionCache();
          const [retryIndex, retryDetail] = await Promise.all([
            fetchPublishedArticles(),
            fetchPublishedArticleById(id),
          ]);
          setPublishedArticles(retryIndex);
          setArticle(retryDetail);
        } else {
          setPublishedArticles(index);
          setArticle(detail);
        }
      } catch (e) {
        console.error('[ArticleDetail] 記事の読み込みに失敗しました', e);
        if (!hadBootstrap) {
          setPublishedArticles([]);
          setArticle(null);
        }
      } finally {
        setLoading(false);
      }
    };
    void loadArticle();
  }, [id]);

  if (loading && !article) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <i className="ri-loader-4-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-600">旅行記事を読み込んでいます...</p>
          </div>
        </main>
        <Footer />
        <StickyCTA />
      </>
    );
  }
  
  // 非公開・不存在は 404 ではなく記事一覧へリダイレクト（仕様）
  if (!id || !article) {
    return <Navigate to="/articles" replace />;
  }

  return (
    <>
      <ArticleDocumentHead article={article} />
      <ArticleJsonLd article={article} />
      {articleBreadcrumbItems && <BreadcrumbJsonLd items={articleBreadcrumbItems} />}
      <Header />
      <main>
        <ArticleContent article={article} />
        <RelatedArticles articles={publishedArticles} />
      </main>
      <Footer />
      <StickyCTA />
    </>
  );
}
